import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormService} from "@core/shared/services/form.service";
import {OfferSearchService} from "@core/shared/services/offer/offer-search.service";
import {OfferListService} from "@core/shared/services/offer/offer-list.service";
import {OfferCardService} from "@core/shared/services/offer/offer-card.service";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {Observable, of} from "rxjs";
import {Society} from "@core/shared/models/society";
import {LOCALE_ITEMS, LocaleItem, TranslationBuilder} from "@core/shared/models/translation";
import {CHANNEL_SORT_OPERATORS, CHANNEL_SORT_ORDER_BY, ChannelSortOperator, ChannelSortOrderBy} from "@core/shared/models/channel-sort";
import {Currency} from "@core/shared/models/currency";
import {SocietyWebsite} from "@core/shared/models/society/society-website";
import {FieldCollection} from "@lib/form/field";
import {ChannelFont} from "@core/shared/models/channel/channel-font";
import {CHANNEL_FONTS} from "@core/shared/data/channel/channel-font";
import {SocietyIp} from "@core/shared/models/society/society-ip";
import {FileConfig} from "@lib/form/fields/file/file.component";
import {ActivatedRoute, Router} from "@angular/router";
import {AbstractControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {TranslateService} from "@ngx-translate/core";
import {ChannelService} from "@core/shared/services/channel.service";
import {ApiService} from "@core/shared/services/api.service";
import {SocietyService} from "@core/shared/services/society.service";
import {WebsiteService} from "@core/shared/services/website.service";
import {SocietyIpService} from "@core/shared/services/society/society-ip.service";
import {SubDomainService} from "@core/shared/services/sub-domain.service";
import {TranslationService} from "@core/shared/services/translation.service";
import {OfferPart} from "@core/components/offer/offer-list/offer-list.component";
import {Offer} from "@core/shared/models/offer";
import {getOfferListHashtagParts} from "@core/shared/utils/offer/offer-list/offer-list-parts/offer-list-hashtag-parts";
import {map, skip, tap} from "rxjs/operators";
import {SubDomainResponse} from "@core/shared/models/subdomain";
import {REGEX_WEBSITE, REGEX_WEBSITE_COMPLETE} from '@core/shared/models/regex';
import {Channel, ChannelIntegrationMode, ChannelMode} from "@core/shared/models/channel";
import {OfferCatalog} from "@core/shared/models/offer/offer-catalog";
import {Role} from "@core/shared/models/role";
import {Access, AccessType} from "@core/shared/models/access";
import {environment} from "../../../../../environments/environment";
import {SelectSearchConfig} from "@lib/form/fields/select-search/select-search.component";
import {ImageConfig} from "@lib/form/fields/image/image.component";
import {SubscriptionTag} from "@core/shared/models/subscription";
import {CrudService} from "@core/shared/services/crud.service";
import {ClipboardService, IClipboardResponse} from "ngx-clipboard";
import {ChannelTranslation} from "@core/shared/models/channel-translation";
import {MatSelectChange} from "@angular/material/select";
import {UserService} from "@core/shared/services/user.service";
import {Pagination} from "@core/shared/models/pagination";
import {SelectSearchConfiguration} from "@core/components/select/select-search/select-search.component";
import {TransportType} from "@core/shared/models/transport";

@Component({
    selector: 'app-core-channel-form',
    templateUrl: './channel-form.component.html',
    styleUrl: './channel-form.component.scss',
    providers: [
        FormService,
        OfferSearchService,
        OfferListService,
        OfferCardService
    ],
    standalone: false
})
export class ChannelFormComponent implements OnInit, AfterViewInit {

    @ViewChild('tabGroup', {static: true}) tabGroup: MatTabGroup;

    @ViewChild('offerCardPreviewTab', {static: false}) offerCardPreviewTabRef: MatTab;

    @ViewChild('offerViewPreviewTab', {static: false}) offerViewPreviewTabRef: MatTab;

    @ViewChild('showcaseReference', { static: false }) showcaseReference: ElementRef<HTMLDivElement>;

    protected readonly ChannelMode = ChannelMode;

    public mode: ChannelMode = null;

    public society: Society = null;

    public channel: Channel = null;

    public translationBuilder: TranslationBuilder;

    public locales$: Observable<LocaleItem[]>;

    public sortsOrderBy$: Observable<ChannelSortOrderBy[]>;

    public sortsOperators$: Observable<ChannelSortOperator[]>;

    public societyWebsites$: Observable<SocietyWebsite[]>;

    public fieldCollection = new FieldCollection();

    public currencies: Currency[] = [];

    public colorBackground: string;

    public colorTitle: string;

    public colorSubTitle: string;

    public colorBackgroundButton: string;

    public colorTextButton: string;

    public colorHighlighting: string;

    public colorDescription: string;

    public fonts: ChannelFont[] = CHANNEL_FONTS;

    public societyIps: SocietyIp[] = [];

    public saveAndContinueConfiguration: { active: boolean, fragment: ('JSIntegration'|'APIIntegration') } = {
        active: false,
        fragment: null
    };

    public faviconConfig: FileConfig;

    public societySearchConfiguration: SelectSearchConfiguration<Society>;

    public societySearchSourceCallback: (search: string) => Observable<Society[]>;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _snackBar: MatSnackBar,
        private _translateService: TranslateService,
        private _channelService: ChannelService,
        private _apiService: ApiService,
        private _clipboardService: ClipboardService,
        private _societyService: SocietyService,
        private _websiteService: WebsiteService,
        private _societyIpService: SocietyIpService,
        public crudService: CrudService,
        public formService: FormService,
        public userService: UserService,
        public offerSearchService: OfferSearchService,
        public offerListService: OfferListService,
        public offerCardService: OfferCardService,
        public subDomainService: SubDomainService,
        public translationService: TranslationService
    ) {}

    ngOnInit(): void {

        this.channel = this.crudService.isUpdateAction ? (this._activatedRoute.snapshot.data as { channel: Channel }).channel : null;

        this.currencies = (this._activatedRoute.snapshot.data as { currencies: Currency[] }).currencies.filter((currency: Currency): boolean => {

            return !['TWD','AED'].includes(currency.code);
        });

        this._initForm();

        if(this.userService.isCurrentUserWithSociety){

            this.mode = ChannelMode.Classic;

            this.society = (this._activatedRoute.snapshot.data as { society: Society }).society;

            this._initData();
        }
        else{

            if(this.crudService.isUpdateAction){

                this.mode = this.channel.isUpdateAllowed ? ChannelMode.Classic : ChannelMode.Restricted;

                this._societyService.getItemAPI(this.channel.society.id).subscribe((society: Society): void => {

                    this.society = society;

                    this._initData();
                });
            }
        }
    }

    ngAfterViewInit() {

        if(this.crudService.isCreateAction){

            return;
        }

        // Redirection automatique sur un onglet

        this._activatedRoute.fragment.subscribe((fragment: ('JSIntegration'|'APIIntegration')): void => {

            switch (fragment) {

                case "JSIntegration":

                    this.tabGroup.selectedIndex = 2;

                    break;

                case "APIIntegration":

                    this.tabGroup.selectedIndex = 2;

                    break;
            }
        });
    }

    private _updateGiftVoucher(): void {

        this.offerSearchService.updateFilter$.next({
            key: 'giftVoucher.enable',
            value: this.form.get('allowGiftVoucher').value && !this.form.get('allowBooking').value && !this.form.get('allowRequest').value && !this.form.get('allowOnlineSale').value
        });
    }

    private _loadSocietyIps(): void {

        this._societyIpService.getItemsAPI(this.society.id).subscribe((items: SocietyIp[]): void => {

            this.societyIps = items;
        });
    }

    private _configureSearchService(): void {

        this.offerSearchService.selectOfferAllowed = this.crudService.isCreateAction ? true : this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API']);
    }

    private _configureListService(): void {

        this.offerListService.hydratePartsCallback = (currentParts: OfferPart[], items: Offer[], reset: boolean): OfferPart[] => {

            const filterKey: string = 'offer.hashtag';

            const hashtagId: number = this.offerSearchService.filterBuilder.hasFilter(filterKey) ? this.offerSearchService.filterBuilder.getFieldByKey(filterKey).value : null;

            return getOfferListHashtagParts(hashtagId, currentParts, items, reset);
        }
    }

    private _configureCardService(): void {

        this.offerCardService.hashtagsDisplayed = true;
    }

    private _initSocietyConfiguration(): void {

        this.societySearchConfiguration = {
            multiple: false,
            enableSearch: true,
            currentSelectionLabel: this._translateService.instant('channel.owner.selection.value'),
            selectOptionActionLabel: this._translateService.instant('channel.owner.value'),
            searchActionLabel: this._translateService.instant('channel.owner.search.action.value'),
            itemLabel: (item: Society): string => {

                return `${item.name}`;
            }
        }

        this.societySearchSourceCallback = (search: string): Observable<Society[]> => {

            const params: string[] = [
                `page=1`,
                'limit=10',
                'sort[name]=ASC',
                `channelRestricted[eq]=${ this.isClassicMode ? 0 : 1 }`
            ];

            if (search && search.length) {

                params.push(...[
                    `name[lk]=${search}`
                ]);
            }

            return this._societyService.getPaginationChannelShortItemsAPI(params).pipe(map((pagination: Pagination<Society>): Society[] => {

                return pagination.items;
            }));
        }
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({
            society: [null],
            marketplace: [null, [Validators.required]],
            locale: [null, [Validators.required]],
            currency: [null, [Validators.required]],
            searchEngine: [false, [Validators.required]],
            offerCatalogs: [[]],
            isDistributor: [false, Validators.required],
            comment: [null],
            integrationMode: ['subDomain'],
            referenced: [false],
            enable: [false, [Validators.required]],
            displayCo: [false],
            canonicalUrl: ['', [(control: UntypedFormControl) => {

                if (!this.form){

                    return null;
                }

                if (this.crudService.isCreateAction && !this.form.get('enableJS').value) {

                    return null;
                }

                if(this.crudService.isUpdateAction && !this.channel.enableJS) {

                    return null;
                }

                if(!control.value || (control.value.length < 1)) {

                    return {
                        'isRequired': {
                            valid: false
                        }
                    }
                }

                if (/^\//g.test(control.value)){

                    return null;
                }

                return {
                    'isPatternInvalid': {
                        valid: false
                    }
                };
            }]],
            translations: new UntypedFormArray([]),
            style: this._createStyleControl(),
            sort: this._formBuilder.group({
                orderBy: [this.crudService.isCreateAction ? 'updatedAt' : null],
                operator: [this.crudService.isCreateAction ? 'DESC' : null]
            }),
            allowBooking: [false],
            allowGiftVoucher: [false],
            allowRequest: [false],
            allowOnlineSale: [false],
            allowShowcase: [false],
            displayStartFromPrice: [true, [Validators.required]],
            allowedTransports: [['train-station', 'airport'] as TransportType[]],
            hasExternalLink: [false],
            externalLinkLabel: [null],
            externalLinkUrl: [null],
            picture: this._formBuilder.group({
                copyright: [''],
                image: this._formBuilder.group({
                    image: ['']
                })
            }),
            pictureUrl: [null, [Validators.pattern(REGEX_WEBSITE)]],
            logo: this._formBuilder.group({
                copyright: [''],
                image: this._formBuilder.group({
                    image: ['']
                })
            }),
            logoUrl: [null, [Validators.pattern(REGEX_WEBSITE)]],
            favicon: [null],
            allowMap: [true, [Validators.required]],
            allowedIps: [[], [(control: UntypedFormControl) => {

                if (!this.form){

                    return null;
                }

                if (this.crudService.isCreateAction && !this.form.get('enableApi').value) {

                    return null;
                }

                if(this.crudService.isUpdateAction && !this.channel.enableApi){

                    return null;
                }

                if(Boolean(control.value.length)){

                    return null;
                }

                return {
                    'isRequired': {
                        valid: false
                    }
                };
            }]]
        }, { validators: [(form: UntypedFormGroup) => {

                if(form.get('allowBooking').value || form.get('allowGiftVoucher').value || form.get('allowRequest').value || form.get('allowOnlineSale').value || form.get('allowShowcase').value){

                    return null;
                }

                return {
                    'isMarketingConfigurationRequired': {
                        valid: false
                    }
                };
            }]
        });

        if(this.crudService.isCreateAction){

            this.form.addControl('enableSubDomain', this._formBuilder.control(true, [Validators.required]));

            this.form.addControl('enableJS', this._formBuilder.control(false, [Validators.required]));

            this.form.addControl('enableApi', this._formBuilder.control(false, [Validators.required]));

            this.form.addControl('enableOrchestra', this._formBuilder.control(false, [Validators.required]));

            this.form.addControl('enableExternalPayment', this._formBuilder.control(false, [Validators.required]));

            this.form.addControl('subDomain', this._formBuilder.control(null, [(control: UntypedFormControl) => {

                if (!this.form.get('enableSubDomain').value) {

                    return null;
                }

                return (!control.value?.length) ? {required: {valid: false}} : null;

            }], [(control: UntypedFormControl) => {

                if (control.value) {

                    if (!/^[a-z0-9-]*$/.test(control.value)) {

                        return of({isPatternInvalid: {valid: false}});
                    }

                    return this.subDomainService.isValidDomain({'subDomain': control.value}).pipe(
                        map((data: SubDomainResponse) => {

                            return !control.value ? null : (data.isValid ? null : {isNotValid: {valid: false}});
                        })
                    );

                } else {

                    return of(null);
                }
            }]));
        }

        if(this.crudService.isUpdateAction){

            this.sortForm.addControl('id', this._formBuilder.control(''));
        }

        this.fieldCollection.addField({
            type: 'select-search',
            config: {
                id: 'font',
                attrs: {
                    label: this._translateService.instant('channel.fields.font.value'),
                    required: true,
                    choices: this.fonts,
                    multiple: false
                }
            }
        });

        this._initTranslationsForm();

        this.formService.submitCallback = (): void => {

            const formValue: Channel = {...this.form.getRawValue()};

            const data: object = Object.assign(formValue, {
                picture: (!this.form.get('picture').value.image.image) ? null : this.form.get('picture').value,
                pictureUrl: this.hasImage(this.form.get('picture')) ? this.form.get('pictureUrl').value : null,
                favicon:(!this.form.get('favicon').value) ? null : this.form.get('favicon').value,
                logo: (!this.form.get('logo').value.image.image) ? null :this.form.get('logo').value,
                logoUrl: this.hasImage(this.form.get('logo')) ? this.form.get('logoUrl').value : null,
                currency: {id: parseInt(this.form.get('currency').value)},
                marketplace: this.form.get('marketplace').value,
                enable: ('enable' in this.form.value) ? this.form.get('enable').value : false,
                allowBooking: (this.isClassicMode && this.userService.isCurrentUserAdmin) || this.society.channelConfiguration.hasAccessCreateChannelBooking ? formValue.allowBooking : false,
                allowRequest: (this.isClassicMode && this.userService.isCurrentUserAdmin) || this.society.channelConfiguration.hasAccessCreateChannelRequest ? formValue.allowRequest : false,
                allowOnlineSale: (this.isClassicMode && this.userService.isCurrentUserAdmin) || this.society.channelConfiguration.hasAccessCreateChannelOnlineSale ? formValue.allowOnlineSale : false,
                allowGiftVoucher: (this.isClassicMode && this.userService.isCurrentUserAdmin) || this.society.channelConfiguration.hasAccessCreateChannelGiftVoucher ? formValue.allowGiftVoucher : false,
                allowShowcase: (this.isClassicMode && this.userService.isCurrentUserAdmin) || this.society.channelConfiguration.hasAccessCreateChannelShowcase ? formValue.allowShowcase : false
            } as Partial<Channel>);

            if(this.crudService.isCreateAction){

                this._channelService.createSocietyItemAPI(this.society.id, data).subscribe((channel: { id: number }): void => {

                    this._snackBar.open(this._translateService.instant('channel.add.success.value'), this._translateService.instant('notification.close.action.value'), {
                        duration: 5000
                    });

                    if (this.saveAndContinueConfiguration.active) {

                        this._router.navigate(['account/channel/update', channel.id], {fragment: this.saveAndContinueConfiguration.fragment});

                    } else {

                        this.redirectToList();
                    }

                }, (): void => {

                    this.saveAndContinueConfiguration = {
                        active: false,
                        fragment: null
                    };
                });
            }

            if(this.crudService.isUpdateAction){

                this._channelService.updateItemAPI(this.channel.id, data).subscribe((): void => {

                    this._snackBar.open(this._translateService.instant('channel.update.success.value'), this._translateService.instant('notification.close.action.value'), {
                        duration: 5000
                    });

                    if (!this.saveAndContinueConfiguration.active) {

                        this.redirectToList();
                    }
                });
            }
        };
    }

    private _initData(): void {

        this._initLocales();

        this._initSorts();

        this._initFaviconConfigs();

        this._hydrateForm();

        this._loadSocietyWebsites();

        if(this.society.channelConfiguration.hasAccessAPI){

            this._loadSocietyIps();
        }

        if(this.crudService.isCreateAction){

            if(this.isClassicMode){

                this._configureSearchService();

                this._configureListService();

                this._configureCardService();
            }
        }

        if(this.crudService.isUpdateAction){

            this._handleClipboardResponse();

            if(this.isClassicMode){

                this._configureSearchService();

                this._configureListService();

                this._configureCardService();

                const allConfigurationFieldsChecked: boolean = [!this.channel.allowBooking, !this.channel.allowRequest, !this.channel.allowOnlineSale, this.channel.allowGiftVoucher].every((value: boolean): boolean => {

                    return value;
                });

                if(!allConfigurationFieldsChecked){

                    this._updateGiftVoucher();
                }
            }

            this.colorBackground = this.channel.style.colorBackground;

            this.colorTitle = this.channel.style.colorTitle;

            this.colorSubTitle = this.channel.style.colorSubTitle;

            this.colorBackgroundButton = this.channel.style.colorBackgroundButton;

            this.colorTextButton = this.channel.style.colorTextButton;

            this.colorHighlighting = this.channel.style.colorHighlighting;

            this.colorDescription = this.channel.style.colorDescription;

            this.offerSearchService.selectedOffers.next(this.channel.offerCatalogs.map((item: OfferCatalog): Offer => {

                return Object.assign(item.offer, {
                    offerCatalog: {
                        id: item.id
                    }
                } as Offer);
            }));

            this.offerSearchService.displayReferencedOffer = this.form.get('referenced').value;
        }

        this._initEvents();
    }

    private _initEvents(): void {

        this.form.get('isDistributor').valueChanges.subscribe((value: boolean): void => {

            if(this.isClassicMode && this.userService.isCurrentUserAdmin){

                return;
            }

            if(value && (!this.isAllowGiftVoucher || !this.isAvailableGiftVoucher)) {

                this.form.get('allowBooking').patchValue(true);

                this.form.get('allowGiftVoucher').patchValue(false);
            }

            if(this.hasAccessOnlineSale && this.societyHasOneOfThisRoles(['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR']) && !this.form.get('isDistributor').value && this.form.get('allowBooking').value){

                this.form.get('allowOnlineSale').patchValue(true);
            }

            if(this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']) && !this.form.get('isDistributor').value && this.form.get('allowBooking').value){

                this.form.get('allowOnlineSale').patchValue(true);
            }

            this._handleFormMarketingOptionStates();

            if(this.crudService.isUpdateAction){

                value ? this.form.get('currency').enable() : this.form.get('currency').disable();
            }

            this.offerSearchService.updateExtraData('isDistributor', value);

            this.offerSearchService.updateFilter$.next({
                key: 'isDistributor',
                value: value
            });
        });

        [this.form.get('allowBooking'), this.form.get('allowGiftVoucher'), this.form.get('allowRequest'), this.form.get('allowOnlineSale')].forEach((control: AbstractControl): void => {

            control.valueChanges.subscribe((): void => {

                if(this.hasAccessOnlineSale && this.societyHasOneOfThisRoles(['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR']) && !this.form.get('isDistributor').value && this.form.get('allowBooking').value){

                    this.form.get('allowOnlineSale').patchValue(true, {emitEvent: false, onlySelf: true});
                }

                if(this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']) && !this.form.get('isDistributor').value && this.form.get('allowBooking').value){

                    this.form.get('allowOnlineSale').patchValue(true, {emitEvent: false, onlySelf: true});
                }

                const isOnlyOnlineSale: boolean = [
                    this.form.get('allowOnlineSale').value,
                    [this.form.get('allowBooking'), this.form.get('allowGiftVoucher'), this.form.get('allowRequest')].every((control: AbstractControl): boolean => { return !control.value; })
                ].every((condition: boolean): boolean => {

                    return Boolean(condition);
                });

                this.offerSearchService.updateFilter$.next({
                    key: 'permanentOptions.offerPermanentOption.locales',
                    value: isOnlyOnlineSale
                });

                this.offerSearchService.updateFilter$.next({
                    key: 'onlineSale.enable',
                    value: isOnlyOnlineSale
                });
            });

            control.valueChanges.subscribe((): void => {

                this._updateGiftVoucher();
            });
        });

        [this.form.get('allowOnlineSale'), this.form.get('allowGiftVoucher')].forEach((control: AbstractControl): void => {

            control.valueChanges.subscribe((): void => {

                this._handleCurrencyValueFromMarketingOptions();

                this._handleCurrencyStateFromMarketingOptions();
            });
        });

        this.form.get('allowBooking').valueChanges.subscribe((): void => {

            this._handleFormMarketingOptionStates();
        });

        this.form.get('allowShowcase').valueChanges.subscribe((allowShowcase: boolean): void => {

            if(allowShowcase){

                this.form.get('allowOnlineSale').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowBooking').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowRequest').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowGiftVoucher').disable({emitEvent: false, onlySelf: true});
            }
            else{

                this.form.get('allowBooking').enable({emitEvent: false, onlySelf: true});

                this.form.get('allowRequest').enable({emitEvent: false, onlySelf: true});

                this._handleFormMarketingOptionStates();
            }

            this.form.get('allowOnlineSale').patchValue(false, {emitEvent: false, onlySelf: true});

            this.form.get('allowBooking').patchValue(false, {emitEvent: false, onlySelf: true});

            this.form.get('allowRequest').patchValue(false, {emitEvent: false, onlySelf: true});

            this.form.get('allowGiftVoucher').patchValue(false, {emitEvent: false, onlySelf: true});

            this.form.get('displayStartFromPrice').patchValue(true);

            this.offerSearchService.updateExtraData('allowShowcase', allowShowcase);

            if(allowShowcase){

                setTimeout((): void => {

                    this.scrollToBottomOfElement(this.showcaseReference);
                });
            }
        });

        this.offerSearchService.selectedOffers.pipe(skip(1)).subscribe((selectedItems: Offer[]): void => {

            if(this.isClassicMode && this.userService.isCurrentUserAdmin){

                return;
            }

            this.form.get('offerCatalogs').patchValue(selectedItems.map((selectedItem: Offer): Partial<OfferCatalog> => {

                return {
                    id: selectedItem.offerCatalog.id
                };
            }));
        });

        if(this.crudService.isCreateAction){

            this.form.get('referenced').valueChanges.subscribe((value: boolean): void => {

                if(this.isClassicMode && this.userService.isCurrentUserAdmin){

                    return;
                }

                this.offerSearchService.selectedOffers.next([]);

                this.offerSearchService.displayReferencedOffer = value;
            });

            this.form.get('enableJS').valueChanges.subscribe((value: boolean): void => {

                if (!value) {

                    this.form.get('canonicalUrl').patchValue('');
                }

                this.form.get('canonicalUrl').updateValueAndValidity();
            });
        }

        this.form.get('integrationMode').valueChanges.subscribe((value: ChannelIntegrationMode[]): void => {

            if(value.includes('orchestra')) {

                const currency: Currency = this.currencies.find((item: Currency): boolean => {

                    return item.code === 'EUR';
                });

                this.form.get('currency').patchValue(currency.id);

                this.form.get('currency').disable();

            } else {

                this.form.get('currency').enable();
            }
        });

        this.form.get('hasExternalLink').valueChanges.subscribe((): void => {

            const hasExternalLink: boolean = this.form.get('hasExternalLink').value;

            this.form.get('externalLinkLabel').patchValue(null);
            this.form.get('externalLinkLabel').setValidators(hasExternalLink ? [Validators.required] : []);
            this.form.get('externalLinkLabel').updateValueAndValidity();

            this.form.get('externalLinkUrl').patchValue(null);
            this.form.get('externalLinkUrl').setValidators(hasExternalLink ? [Validators.required, Validators.pattern(REGEX_WEBSITE_COMPLETE)] : []);
            this.form.get('externalLinkUrl').updateValueAndValidity();
        });

        this.form.get('currency').valueChanges.subscribe((): void => {

            this._handleFormMarketingOptionValuesFromCurrency();

            this._handleFormMarketingOptionStates();
        });
    }

    private _initTranslationsForm(): void {

        this.translationBuilder = new TranslationBuilder(this._formBuilder);

        this.translationBuilder.form = this.form;

        this.translationBuilder.addItemCallback = (): UntypedFormGroup => {

            const group: FormGroup = this._formBuilder.group({
                name: ['', [Validators.required]],
                title: [null],
                description: [null]
            });

            if(this.crudService.isUpdateAction){

                group.addControl('id', this._formBuilder.control(null));
            }

            return group;
        };

        if(this.crudService.isCreateAction){

            this.translationBuilder.addDefaultItem();
        }
    }

    private _createStyleControl(): FormGroup {

        const group: FormGroup = this._formBuilder.group({
            font: [this.crudService.isCreateAction  ? 'font-lato' : '', Validators.required],
            colorBackground: [null],
            colorTitle: [null],
            colorSubTitle: [null],
            colorDescription: [null],
            colorBackgroundButton: [null],
            colorTextButton: [null],
            colorHighlighting: [null]
        });

        if(this.crudService.isUpdateAction){

            group.addControl('id', this._formBuilder.control(null));
        }

        return group;
    }

    private _initSorts(): void {

        this.sortsOrderBy$ = of(CHANNEL_SORT_ORDER_BY);

        this.sortsOperators$ = of(CHANNEL_SORT_OPERATORS);
    }

    private _initLocales(): void {

        this.locales$ = of(LOCALE_ITEMS);
    }

    private _loadSocietyWebsites(): void {

        this.societyWebsites$ = this._websiteService.getSocietyItemsAPI(this.society.id).pipe(
            tap((societyWebsites: SocietyWebsite[]): void => {

                const societyWebsite: SocietyWebsite = societyWebsites.find((item: SocietyWebsite): boolean => {

                    return item.website === this.society.mainWebsite;

                }) || null;

                this.form.get('marketplace').patchValue(this.crudService.isCreateAction ? societyWebsite : (this.hasAccessAdditionalWebsite ? this.channel.marketplace : societyWebsite));
            })
        );
    }

    private _hydrateForm(): void {

        if(this.crudService.isCreateAction){

            // Mise à jour des données

            this.form.patchValue({
                enable: this.isActivationAllowed
            });

            this._handleIsDistributorValue();

            this._handleFormMarketingOptionValues();

            this._handleCurrencyValueFromMarketingOptions();

            // Mise à jour des statuts

            this._handleIsDistributorState();

            this.isActivationAllowed ? this.form.get('enable').enable() : this.form.get('enable').disable();

            this.hasAccessAdditionalWebsite ? this.form.get('marketplace').enable() : this.form.get('marketplace').disable();
        }

        if(this.crudService.isUpdateAction){

            // Mise à jour des données

            this.form.patchValue(Object.assign({...this.channel}, {
                currency: this.channel.currency.id,
                allowGiftVoucher: this.isAllowGiftVoucher ? this.channel.allowGiftVoucher : false,
                allowOnlineSale: this.isAllowOnlineSale ? this.channel.allowOnlineSale : false,
                integrationMode: this.integrationMode,
                picture: this.channel.picture || {
                    copyright: '',
                    image: {
                        image:''
                    }
                },
                logo: this.channel.logo || {
                    copyright: '',
                    image: {
                        image:''
                    }
                }
            }));

            // Mise à jour des statuts

            if(this.channel.allowShowcase){

                this.form.get('allowOnlineSale').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowBooking').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowRequest').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowGiftVoucher').disable({emitEvent: false, onlySelf: true});

                this.offerSearchService.updateExtraData('allowShowcase', true);
            }

            if(this.channel.enableOrchestra) {

                this.offerSearchService.updateExtraData('channelId', this.channel.id);
                this.offerSearchService.updateExtraData('isChannelOrchestra', true);
            }

            this.form.get('canonicalUrl').disable();

            this.form.get('integrationMode').disable();

            this.form.get('referenced').disable();

            if((this.isOwnerAdmin && this.isOneOfTheseIntegrationModes(['subDomain', 'JS'])) || (this.societyHasRole('ROLE_OFFER_CREATOR') && !this.societyHasRole('ROLE_OFFER_DISTRIBUTOR'))){

                this.form.get('isDistributor').disable();
            }

            if(this.userService.isCurrentUserWithSociety && !this.societyHasAccess('CHANNEL_CREATE_IS_MINE') && !this.societyHasAccess('CHANNEL_DELETE_IS_MINE')){

                this.form.get('locale').disable();

                this.form.get('isDistributor').disable();
            }

            if(this.societyHasOneOfThisRoles(['ROLE_OFFER_DISTRIBUTOR'])){

                this.form.get('isDistributor').disable();
            }

            if(this.userService.isCurrentUserAdmin || (this.userService.isCurrentUserWithSociety && this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API']))){

                this.isDistributor ? this.form.get('currency').enable() : this.form.get('currency').disable();
            }

            if(this.isOneOfTheseIntegrationModes(['orchestra'])){

                if(this.userService.isCurrentUserWithSociety){

                    this.form.get('locale').disable();
                }

                this.form.get('currency').disable();
            }

            this.isActivationAllowed ? this.form.get('enable').enable() : this.form.get('enable').disable();

            this.hasAccessAdditionalWebsite ? this.form.get('marketplace').enable() : this.form.get('marketplace').disable();

            this.channel.translations.forEach((translation: ChannelTranslation): void => {

                const control: FormGroup = this.translationBuilder.addItemControl(this.translationBuilder.getLocaleItem(translation.locale), translation);

                if(this.userService.isCurrentUserWithSociety && this.isClassicMode && !this.societyHasAccess('CHANNEL_CREATE_IS_MINE') && !this.societyHasAccess('CHANNEL_DELETE_IS_MINE')){

                    control.get('name').disable();

                    control.get('title').disable();
                }
            });
        }

        this._handleSortState();

        this._handleFormMarketingOptionStates();

        this._handleCurrencyStateFromMarketingOptions();

        this.offerSearchService.updateExtraData('isDistributor', this.form.get('isDistributor').value);
    }

    private _handleFormMarketingOptionValues(): void {

        if(this.userService.isCurrentUserWithSociety || this.isRestrictedMode){

            this.form.get('allowBooking').patchValue(true);

            this.form.get('allowRequest').patchValue(true);

            this.form.get('allowGiftVoucher').patchValue(this.isAllowGiftVoucher);

            this.form.get('allowOnlineSale').patchValue(this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']));

            this.form.get('allowShowcase').patchValue(false);
        }
        else{

            this.form.get('allowBooking').patchValue(this.isOneOfTheseIntegrationModes(['orchestra']) ? false : this.society.channelConfiguration.hasAccessCreateChannelBooking, { emitEvent: false });

            this.form.get('allowRequest').patchValue(this.isOneOfTheseIntegrationModes(['orchestra']) ? false : this.society.channelConfiguration.hasAccessCreateChannelRequest, { emitEvent: false });

            this.form.get('allowGiftVoucher').patchValue(this.isOneOfTheseIntegrationModes(['orchestra']) ? true : this.society.channelConfiguration.hasAccessCreateChannelGiftVoucher, { emitEvent: false });

            this.form.get('allowOnlineSale').patchValue(this.isOneOfTheseIntegrationModes(['orchestra']) ? true : this.society.channelConfiguration.hasAccessCreateChannelOnlineSale, { emitEvent: false });

            this.form.get('allowShowcase').patchValue(false, { emitEvent: false });
        }
    }

    private _handleFormMarketingOptionStates(): void {

        if(this.userService.isCurrentUserWithSociety || this.isRestrictedMode){

            this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API']) && this.isAllowOnlineSaleEnabled && !this.form.get('allowShowcase').value ? this.form.get('allowOnlineSale').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowOnlineSale').disable({emitEvent: false, onlySelf: true});

            this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API']) && this.isAllowGiftVoucher && !this.form.get('allowShowcase').value ? this.form.get('allowGiftVoucher').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowGiftVoucher').disable({emitEvent: false, onlySelf: true});

            this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API']) && this.society.channelConfiguration.hasAccessCreateChannelBooking && !this.form.get('allowShowcase').value ? this.form.get('allowBooking').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowBooking').disable({emitEvent: false, onlySelf: true});

            this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API']) && this.society.channelConfiguration.hasAccessCreateChannelRequest && !this.form.get('allowShowcase').value ? this.form.get('allowRequest').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowRequest').disable({emitEvent: false, onlySelf: true});

            this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API']) && this.society.channelConfiguration.hasAccessCreateChannelShowcase ? this.form.get('allowShowcase').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowShowcase').disable({emitEvent: false, onlySelf: true});
        }
        else{

            if(this.isOneOfTheseIntegrationModes(['subDomain', 'JS', 'API'])){

                this.society.channelConfiguration.hasAccessCreateChannelBooking && !this.form.get('allowShowcase').value ? this.form.get('allowBooking').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowBooking').disable({emitEvent: false, onlySelf: true});

                this.society.channelConfiguration.hasAccessCreateChannelRequest && !this.form.get('allowShowcase').value ? this.form.get('allowRequest').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowRequest').disable({emitEvent: false, onlySelf: true});

                this.society.channelConfiguration.hasAccessCreateChannelOnlineSale && !this.form.get('allowShowcase').value ? this.form.get('allowOnlineSale').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowOnlineSale').disable({emitEvent: false, onlySelf: true});

                this.society.channelConfiguration.hasAccessCreateChannelGiftVoucher && !this.form.get('allowShowcase').value ? this.form.get('allowGiftVoucher').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowGiftVoucher').disable({emitEvent: false, onlySelf: true});

                this.society.channelConfiguration.hasAccessCreateChannelShowcase ? this.form.get('allowShowcase').enable({emitEvent: false, onlySelf: true}) : this.form.get('allowShowcase').disable({emitEvent: false, onlySelf: true});
            }
            else{

                this.form.get('allowBooking').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowRequest').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowShowcase').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowGiftVoucher').disable({emitEvent: false, onlySelf: true});

                this.form.get('allowOnlineSale').disable({emitEvent: false, onlySelf: true});
            }
        }

        this._handleFormMarketingOptionStatesFromCurrency();
    }

    private _handleCurrencyValueFromMarketingOptions(): void {

        const controls: AbstractControl[] = [
            this.form.get('allowGiftVoucher'),
            this.form.get('allowOnlineSale'),
        ];

        const isCurrencySelectionAllowed: boolean = controls.every((control: AbstractControl): boolean => {

            return !control.value;
        });

        if(!isCurrencySelectionAllowed) {

            const currency: Currency = this.currencies.find((item: Currency): boolean => {

                return item.code === 'EUR';
            });

            this.form.get('currency').patchValue(currency.id, {emitEvent: false, onlySelf: true});
        }
    }

    private _handleCurrencyStateFromMarketingOptions(): void {

        const controls: AbstractControl[] = [
            this.form.get('allowGiftVoucher'),
            this.form.get('allowOnlineSale'),
        ];

        const isCurrencySelectionAllowed: boolean = controls.every((control: AbstractControl): boolean => {

            return !control.value;
        });

        if(isCurrencySelectionAllowed) {

            this.form.get('currency').enable({emitEvent: false, onlySelf: true});

        } else {

            this.form.get('currency').disable({emitEvent: false, onlySelf: true});
        }
    }

    private _initFaviconConfigs(): void {

        this._translateService.get('file.extension.list.allowed.value', {list: '.ico'}).subscribe((help: string): void => {

            this.faviconConfig = {
                id: 'favicon',
                gallery: {
                    id: null,
                    type: 'file',
                    context: 'channel_favicon'
                },
                required: false,
                uploadApiUrl: this._apiService.getApiUrl(false, true),
                help: help,
                upload: {
                    maxSize : 100000,
                    allowedTypes: ['image/x-icon'].join(',')
                }
            };
        });
    }

    private _handleSortValue(): void {

        this.sortForm.get('orderBy').patchValue(this.isOneOfTheseIntegrationModes(['orchestra']) ? null : 'updatedAt');

        this.sortForm.get('operator').patchValue(this.isOneOfTheseIntegrationModes(['orchestra']) ? null : 'DESC');
    }

    private _handleSortState(): void {

        const validators: ValidatorFn[] = [];

        if(!this.isOneOfTheseIntegrationModes(['orchestra'])){

            validators.push(Validators.required);
        }

        // Tri des offres par

        this.sortForm.get('orderBy').setValidators(validators);

        this.sortForm.get('orderBy').updateValueAndValidity();

        // Sens du tri

        this.sortForm.get('operator').setValidators(validators);

        this.sortForm.get('operator').updateValueAndValidity();
    }

    private _handleIsDistributorValue(): void {

        if(this.userService.isCurrentUserWithSociety || this.isRestrictedMode){

            this.form.get('isDistributor').patchValue(!this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']));
        }
        else{

            switch (true){

                case this.societyHasRole('ROLE_OFFER_DISTRIBUTOR'):

                    this.form.get('isDistributor').patchValue(true);

                    break;

                case this.societyHasRole('ROLE_OFFER_CREATOR') && !this.societyHasRole('ROLE_OFFER_DISTRIBUTOR'):

                    this.form.get('isDistributor').patchValue(true);

                    break;

                case this.societyHasAllOfThisRoles(['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR']) && this.isOneOfTheseIntegrationModes(['orchestra']):

                    this.form.get('isDistributor').patchValue(true);

                    break;

                case this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']):

                    this.form.get('isDistributor').patchValue(false);

                    break;

                default:

                    this.form.get('isDistributor').patchValue(false);
            }
        }
    }

    private _handleIsDistributorState(): void {

        if(this.userService.isCurrentUserWithSociety || this.isRestrictedMode){

            if(this.societyHasOneOfThisRoles(['ROLE_OFFER_DISTRIBUTOR', 'ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION'])){

                this.form.get('isDistributor').disable();
            }
        }
        else{

            switch (true){

                case this.societyHasRole('ROLE_OFFER_CREATOR') && !this.societyHasRole('ROLE_OFFER_DISTRIBUTOR'):

                    this.form.get('isDistributor').disable();

                    break;

                case this.societyHasAllOfThisRoles(['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR']) && this.isOneOfTheseIntegrationModes(['orchestra']):

                    this.form.get('isDistributor').disable();

                    break;

                case this.societyHasOneOfThisRoles(['ROLE_OFFER_DISTRIBUTOR', 'ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']):

                    this.form.get('isDistributor').disable();

                    break;

                default:

                    this.form.get('isDistributor').enable();
            }
        }
    }

    private _handleFormMarketingOptionValuesFromCurrency(): void {

        const currentCurrency: Currency = this.currencies.find((item: Currency): boolean => {

            return item.id === parseInt(this.form.get('currency').value);
        });

        if(!currentCurrency){

            return;
        }

        const allowedCurrency: Currency = this.currencies.find((item: Currency): boolean => {

            return item.code === 'EUR';
        });

        const isAllowedCurrency: boolean = currentCurrency.id === allowedCurrency.id;

        if(isAllowedCurrency){

            if(this.crudService.isCreateAction){

                // Création d'un canal

                this.form.get('allowOnlineSale').patchValue(false, {emitEvent: false, onlySelf: true});

                this.form.get('allowGiftVoucher').patchValue(false, {emitEvent: false, onlySelf: true});
            }

            if(this.crudService.isUpdateAction){

                // Mise à jour d'un canal

                this.form.get('allowOnlineSale').patchValue(this.isAllowOnlineSale ? this.channel.allowOnlineSale : false, {emitEvent: false, onlySelf: true});

                this.form.get('allowGiftVoucher').patchValue(this.isAllowGiftVoucher ? this.channel.allowGiftVoucher : false, {emitEvent: false, onlySelf: true});
            }
        }
        else {

            this.form.get('allowOnlineSale').patchValue(false, {emitEvent: false, onlySelf: true});

            this.form.get('allowGiftVoucher').patchValue(false, {emitEvent: false, onlySelf: true});
        }
    }

    private _handleFormMarketingOptionStatesFromCurrency(): void {

        const currentCurrency: Currency = this.currencies.find((item: Currency): boolean => {

            return item.id === parseInt(this.form.get('currency').value);
        });

        if(!currentCurrency){

            return;
        }

        const allowedCurrency: Currency = this.currencies.find((item: Currency): boolean => {

            return item.code === 'EUR';
        });

        const isAllowedCurrency: boolean = currentCurrency.id === allowedCurrency.id;

        if(isAllowedCurrency){

            return;
        }
        else {

            this.form.get('allowOnlineSale').disable({emitEvent: false, onlySelf: true});

            this.form.get('allowGiftVoucher').disable({emitEvent: false, onlySelf: true});
        }
    }

    private _handleClipboardResponse(): void {

        this._clipboardService.copyResponse$.subscribe((response: IClipboardResponse): void => {

            if (response.isSuccess) {

                this._snackBar.open(this._translateService.instant('content.copied.value'), this._translateService.instant('notification.close.action.value'), {
                    duration: 5000
                });
            }
        });
    }

    public redirectToList(): void {

        this._router.navigate(['account/channel/list']);
    }

    public getTranslation(index: number): UntypedFormGroup {

        return this.translationsControl.controls[index] as UntypedFormGroup;
    }

    public societyHasRole(role: Role): boolean {

        if(!this.society){

            return false;
        }

        return this.society.admin.roles.includes(role);
    }

    public societyHasOneOfThisRoles(roles: Role[]): boolean {

        return roles.some((role: Role): boolean => {

            return this.societyHasRole(role);
        });
    }

    public societyHasAllOfThisRoles(roles: Role[]): boolean {

        return roles.every((role: Role): boolean => {

            return this.societyHasRole(role);
        });
    }

    public updateIntegrationMode(): void {

        if(this.crudService.isUpdateAction){

            return;
        }

        const integrationMode: ChannelIntegrationMode = this.form.get('integrationMode').value;

        this.form.get('enableSubDomain').patchValue(integrationMode === 'subDomain');

        this.form.get('enableJS').patchValue(integrationMode === 'JS');

        this.form.get('enableApi').patchValue(integrationMode === 'API');

        this.form.get('enableOrchestra').patchValue(integrationMode === 'orchestra');

        this.form.get('enableExternalPayment').patchValue(integrationMode === 'orchestra');

        this.form.get('subDomain').patchValue(null);

        this.form.get('referenced').patchValue(false);

        this.form.get('allowedIps').patchValue([]);

        this.form.get('favicon').patchValue(null);

        this._handleSortValue();

        this._handleSortState();

        this._handleIsDistributorValue();

        this._handleIsDistributorState();

        this._handleFormMarketingOptionValues();

        this._handleFormMarketingOptionStates();
    }

    public submitEnter(event: KeyboardEvent): void {

        if (event.key === 'Enter') {

            event.preventDefault();

            return;
        }
    }

    public submit(saveAndContinue: boolean, fragment?: ('JSIntegration'|'APIIntegration')): void {

        this.saveAndContinueConfiguration = {
            active: saveAndContinue,
            fragment: fragment
        };

        this.formService.submit();
    }

    public changeFont(event: MatSelectChange): void {

        this.form.controls['style'].get('font').patchValue(event.value);
    }

    public updateColorField(value: string, field: string): void {

        this.form.controls['style'].get(field).patchValue(value);
    }

    public changeLocale(): void {

        this.translationBuilder.changeLocale([this.form.get('locale').value]);

        if(this.form.get('locale').invalid){

            return;
        }

        const offers: Offer[] = [...this.offerSearchService.selectedOffers.getValue()];

        this.offerSearchService.selectedOffers.next(offers.filter((offer: Offer): boolean => {

            return offer.locales.includes(this.form.get('locale').value);
        }));
    }

    public deselectOffer(): void {

        this.offerSearchService.selectedOffers.next([]);
    }

    public copyJSIntegrationWCContent(): void {

        this._clipboardService.copy(`<tywin-channel token="${this.channel.tokenJs}" base-url="${this.canonicalUrl}"></tywin-channel>`);
    }

    public copyJSIntegrationSourceContent(): void {

        this._clipboardService.copy(this.JSIntegrationSourceTag);
    }

    public compareSocietyWebsite(a: SocietyWebsite, b: SocietyWebsite): boolean {

        if(!a || !b){

            return false;
        }

        return a.id === b.id;
    }

    public compareSocietyIp(a: SocietyIp, b: SocietyIp): boolean {

        if(!a || !b){

            return false;
        }

        return a.id === b.id;
    }

    public editCanonicalUrl(): void {

        if (this.channel.enableJS) {

            this.form.get('canonicalUrl').enable();
        }
    }

    public hasImage(formControl: AbstractControl): boolean {

        if(!this.form) {

            return false;
        }

        const imageControl: { image: object } = formControl.get('image').value;

        return Boolean(!!imageControl.image && imageControl.image.hasOwnProperty('id'));
    }

    public orderByLabel(orderBy: ChannelSortOrderBy): string {

        return this._translateService.instant(`channel.sort.orderBy.${orderBy}`);
    }

    public operatorLabel(operator: ChannelSortOperator): string {

        return this._translateService.instant(`channel.sort.operator.${operator.toLowerCase()}`);
    }

    public isOneOfTheseIntegrationModes(items: ChannelIntegrationMode[]): boolean {

        const integrationMode: ChannelIntegrationMode = this.form.get('integrationMode').value;

        return items.includes(integrationMode);
    }

    public scrollToBottomOfElement(element: ElementRef): void {

        if(!element || !element.nativeElement){

            return;
        }

        element.nativeElement.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest"
        });
    }

    public downloadTechnicalDocumentation(): void {

        this._channelService.downloadTechnicalDocumentation(this.localeId).subscribe(blob => {

            const a: HTMLAnchorElement = document.createElement('a');

            const objectUrl: string = URL.createObjectURL(blob);

            a.href = objectUrl;

            a.download = `${this._translateService.instant('channel.technical.documentation.files.title.value')}.pdf`;

            a.click();

            URL.revokeObjectURL(objectUrl);
        });
    }

    public societyHasAccess(access: AccessType): boolean {

        if(!this.society){

            return false;
        }

        const tags: AccessType[] = this.society.admin.accesses.map((access: Access): AccessType => {

            return access.tag;
        });

        return tags.includes(access);
    }

    public handleChannelType(): void {

        this._initSocietyConfiguration();

        if(this.isRestrictedMode){

            this.form.addControl('groupQuota', this._formBuilder.control(0, [Validators.required]));

            this.form.addControl('individualQuota', this._formBuilder.control(0, [Validators.required]));
        }
    }

    public handleSociety(society: Society|Society[]): void {

        this.society = society as Society;

        if(!this.society){

            return;
        }

        this._initData();
    }

    public handleAllowedTransportSelection(type: TransportType, selected: boolean): void {

        const allowedTransports: TransportType[] = this.form.get('allowedTransports').value;

        if (selected) {

            allowedTransports.push(type);

        } else {

            const index: number = allowedTransports.findIndex((item: TransportType): boolean => {

                return item === type;
            });

            allowedTransports.splice(index, 1);
        }

        this.form.get('allowedTransports').patchValue(allowedTransports);
    }

    public isSelectedAllowedTransport(type: TransportType): boolean {

        const allowedTransports: TransportType[] = this.form.get('allowedTransports').value;

        return allowedTransports.includes(type);
    }

    get form(): UntypedFormGroup {

        return this.formService.form;
    }

    get styleForm(): UntypedFormGroup {

        return this.form.get('style') as UntypedFormGroup;
    }

    get sortForm(): UntypedFormGroup {

        return this.form.get('sort') as UntypedFormGroup;
    }

    get translationsControl(): UntypedFormArray {

        return this.form.get('translations') as UntypedFormArray;
    }

    get hasAccessGiftVoucher(): boolean {

        if(!this.society){

            return false;
        }

        const accesses = this.society.admin.accesses.filter((access: Access): boolean => {

            return access.tag === 'ONLINE_SALE';
        });

        return (accesses.length > 0);
    }

    get hasAccessOnlineSale(): boolean {

        if(!this.society){

            return false;
        }

        return this.society.admin.accesses.some((access: Access): boolean => {

            return access.tag === 'ONLINE_SALE';
        });
    }

    get hasAccessUnlimitedChannel(): boolean {

        if(!this.society){

            return false;
        }

        return this.society.admin.accesses.some((access: Access): boolean => {

            return access.tag === 'UNLIMITED_CHANNEL';
        });
    }

    get hasAccessAdditionalWebsite(): boolean {

        if(!this.society){

            return false;
        }

        return this.society.admin.accesses.some((access: Access): boolean => {

            return access.tag === 'ADDITIONAL_WEBSITE';
        });
    }

    get isDistributor(): boolean {

        return this.form.get('isDistributor').value;
    }

    get isAllowGiftVoucher(): boolean {

        if(this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_FEDERATION', 'ROLE_INSTITUTIONAL'])){

            return true;
        }

        if(!this.hasAccessGiftVoucher){

            return false;
        }

        if(this.isDistributor){

            return this.society.isValidMangoPayAccount;
        }

        return true;
    }

    get isAllowOnlineSale(): boolean {

        if(this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_FEDERATION', 'ROLE_INSTITUTIONAL'])){

            return true;
        }

        if(!this.hasAccessOnlineSale){

            return false;
        }

        if(this.isDistributor){

            return this.society.isValidMangoPayAccount;
        }

        return true;
    }

    get isAvailableGiftVoucher(): boolean {

        if (!this.form) {

            return false;
        }

        if(!this.society){

            return false;
        }

        if (this.societyHasRole('ROLE_OFFER_CREATOR')) {

            return this.society.isValidMangoPayAccount;
        }

        if (this.societyHasRole('ROLE_OFFER_DISTRIBUTOR')) {

            return this.society.isValidMangoPayAccount || !this.isDistributor;
        }

        return this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']);
    }

    get isAllowOnlineSaleEnabled(): boolean {

        if(this.societyHasOneOfThisRoles(['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR'])){

            if(!this.hasAccessOnlineSale){

                return false;
            }

            if (this.societyHasRole('ROLE_OFFER_DISTRIBUTOR') && !(this.society.isValidMangoPayAccount)) {

                return false;
            }

            if(!this.form.get('isDistributor').value && this.form.get('allowBooking').value){

                return false;
            }
        }

        if(this.societyHasOneOfThisRoles(['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION'])){

            if(!this.form.get('isDistributor').value && this.form.get('allowBooking').value){

                return false;
            }
        }

        return true;
    }

    get subDomainSuffix(): string {

        return environment.channelSubDomainSuffix;
    }

    get isActivationAllowed(): boolean {

        if(this.crudService.isUpdateAction && (!this.form || !this.form.get('enable'))){

            return false;
        }

        if(!this.society){

            return false;
        }

        if(this.crudService.isUpdateAction && this.channel.enable){

            return true;
        }

        if(this.society.countAllowedChannels === null) {

            return true;
        }

        return this.society.countAllowedChannels > this.society.countEnableChannels;
    }

    get isDefinedMode(): boolean {

        return !!this.mode;
    }

    get isUndefinedMode(): boolean {

        return !this.mode;
    }

    get isClassicMode(): boolean {

        return this.mode === ChannelMode.Classic;
    }

    get isRestrictedMode(): boolean {

        return this.mode === ChannelMode.Restricted;
    }

    get fontFieldConfig(): SelectSearchConfig {

        return this.fieldCollection.getFieldConfig('font') as SelectSearchConfig;
    }

    get marketplace(): string {

        const societyService: SocietyWebsite = (this.form.get('marketplace').value as SocietyWebsite);

        return societyService ? societyService.website : null;
    }

    get imageConfig(): ImageConfig {

        return {
            id: 'image',
            gallery_context: 'channel_landscape',
            required: false,
            uploadApiUrl: this._apiService.getApiUrl(false, true),
            options: {
                enableTitle: false,
                enableSubtitle: false,
                enableAlt: false,
                enableLink: false,
                enableTargetBlank: false
            }
        }
    }

    get logoConfig(): ImageConfig {

        return {
            id: 'image',
            gallery_context: 'channel_logo',
            required: false,
            uploadApiUrl: this._apiService.getApiUrl(false, true),
            options: {
                enableTitle: false,
                enableSubtitle: false,
                enableAlt: false,
                enableLink: false,
                enableTargetBlank: false
            }
        }
    }

    get logoImageControl(): FormGroup {

        return this.form.get('logo').get('image') as FormGroup;
    }

    get pictureImageControl(): FormGroup {

        return this.form.get('picture').get('image') as FormGroup;
    }

    get isStarterSubscription(): boolean {

        if(this.userService.isCurrentUserAdmin){

            return false;
        }

        return this.society.subscriptionTag === SubscriptionTag.Starter;
    }

    get integrationMode(): ChannelIntegrationMode {

        if(!this.channel){

            return null;
        }

        const items: { [p in ChannelIntegrationMode]: boolean } = {
            subDomain: this.channel.enableSubDomain,
            JS: this.channel.enableJS,
            API: this.channel.enableApi,
            orchestra: this.channel.enableOrchestra
        };

        return Object.keys(items).find((key: ChannelIntegrationMode): boolean => {

            return Boolean(items[key]);

        }) as ChannelIntegrationMode;
    }

    get canonicalUrl(): string {

        const canonicalUrl: string = (this.form.get('canonicalUrl').value as string);

        return `${this.marketplace}${canonicalUrl}`;
    }

    get JSIntegrationSourceTag(): string {

        return `<script src="${environment.channelBaseUrl}/build/channel/channel.js" defer></script>`;
    }

    get subDomainIntegrationLink(): string {

        return `https://${this.channel.subDomain}${environment.channelSubDomainSuffix}`;
    }

    get localeId(): string {

        return this._translateService.currentLang;
    }

    get allowedIps(): string[] {

        return (this.form.get('allowedIps').value as SocietyIp[]).map((item: SocietyIp): string => {

            return item.address;
        });
    }

    get allowOnlineSaleDisplayed(): boolean {

        if(this.isClassicMode && this.userService.isCurrentUserAdmin && this.isOneOfTheseIntegrationModes(['orchestra'])){

            return true;
        }

        if(this.userService.isCurrentUserWithSociety && this.isOneOfTheseIntegrationModes(['API'])){

            return true;
        }

        return this.society.channelConfiguration.hasAccessCreateChannelOnlineSale;
    }

    get allowBookingDisplayed(): boolean {

        if(this.isClassicMode && this.userService.isCurrentUserAdmin && this.isOneOfTheseIntegrationModes(['orchestra'])){

            return false;
        }

        if(this.userService.isCurrentUserWithSociety && this.isOneOfTheseIntegrationModes(['API'])){

            return true;
        }

        return this.society.channelConfiguration.hasAccessCreateChannelBooking;
    }

    get allowRequestDisplayed(): boolean {

        if(this.isClassicMode && this.userService.isCurrentUserAdmin && this.isOneOfTheseIntegrationModes(['orchestra'])){

            return false;
        }

        if(this.userService.isCurrentUserWithSociety && this.isOneOfTheseIntegrationModes(['API'])){

            return true;
        }

        return this.society.channelConfiguration.hasAccessCreateChannelRequest;
    }

    get allowGiftVoucherDisplayed(): boolean {

        if(this.isClassicMode && this.userService.isCurrentUserAdmin && this.isOneOfTheseIntegrationModes(['orchestra'])){

            return true;
        }

        if(this.userService.isCurrentUserWithSociety && this.isOneOfTheseIntegrationModes(['API'])){

            return true;
        }

        return this.society.channelConfiguration.hasAccessCreateChannelGiftVoucher;
    }

    get allowShowcaseDisplayed(): boolean {

        if(this.isClassicMode && this.userService.isCurrentUserAdmin && this.isOneOfTheseIntegrationModes(['orchestra'])){

            return false;
        }

        if(this.userService.isCurrentUserWithSociety && this.isOneOfTheseIntegrationModes(['API'])){

            return true;
        }

        return this.society.channelConfiguration.hasAccessCreateChannelShowcase;
    }

    get isOwnerAdmin(): boolean {

        return this.userService.isUserAdmin(this.channel.owner);
    }

    get isMine(): boolean {

        if(this.crudService.isCreateAction){

            return false;
        }

        if(this.userService.isCurrentUserAdmin){

            return this.isOwnerAdmin;
        }

        return this.channel.society.id === this.society.id;
    }

    get marketingConfigurationDisplayed(): boolean {

        if(this.allowOnlineSaleDisplayed || this.allowBookingDisplayed || this.allowRequestDisplayed || this.allowGiftVoucherDisplayed) {

            return true;
        }

        return this.allowGiftVoucherDisplayed;
    }

    get marketingRestrictionFromCurrencyDisplayed(): boolean {

        if(!this.form.get('currency').value){

            return false;
        }

        const currentCurrency: Currency = this.currencies.find((item: Currency): boolean => {

            return item.id === parseInt(this.form.get('currency').value);
        });

        return !['EUR'].includes(currentCurrency.code);
    }

    get integrationModeApiDisplayed(): boolean {

        if(this.userService.hasOneOfThisRoles(this.userService.currentUser.getValue(), ['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR'])){

            return true;
        }

        return this.society.channelConfiguration.hasAccessAPI;
    }

    get integrationModeOrchestraDisplayed(): boolean {

        if(this.userService.hasOneOfThisRoles(this.userService.currentUser.getValue(), ['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR'])){

            return true;
        }

        return this.society.channelConfiguration.hasAccessAPI && this.society.channelConfiguration.allowExternalPayment && this.society.channelConfiguration.hasAccessCreateChannelOnlineSale && this.society.channelConfiguration.hasAccessCreateChannelGiftVoucher;
    }
}
